import React, { useCallback, useEffect, useState } from "react";
import { Sunrise, Sprout, ClipboardCheck, FileText, Truck, Store, CheckCircle2, AlertTriangle, ArrowRight, PackageCheck, UserCheck, Printer, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, Badge, Button, PageHeader, PageLoader } from "../lib/ui";
import { fmtNum } from "../lib/constants";
import { useToast } from "../lib/toast";
import { useT } from "../lib/i18n";
import { usePrintDoc } from "../lib/usePrintDoc";
import OpsProgress from "../components/OpsProgress";
import OrderDetailModal from "../components/OrderDetailModal";

const STAGE_TONE = { READY: "info", LOADED: "warn", IN_TRANSIT: "warn" };

function Step({ n, title, icon: Icon, done, children, testid }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <div className="flex items-center gap-3 border-b border-line px-5 py-3.5">
        <span className={`flex h-8 w-8 items-center justify-center rounded-xl text-sm font-extrabold ${done ? "bg-emerald-500 text-white" : "bg-primary-light text-primary"}`}>
          {done ? <CheckCircle2 size={18} /> : n}
        </span>
        <Icon size={18} className="text-ink-soft" />
        <h3 className="font-heading text-base font-extrabold text-ink">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </Card>
  );
}

export default function CentralMorning() {
  const { t } = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { printDoc, exportDoc, printing } = usePrintDoc();
  const [data, setData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [fverify, setFverify] = useState({});

  const load = useCallback(async () => {
    try {
      const [{ data: d }, { data: dr }] = await Promise.all([
        api.get("/workspace/central-morning"), api.get("/workspace/drivers")]);
      setData(d); setDrivers(dr);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Seed received/damaged inputs from server (default received = ordered) without clobbering edits.
  useEffect(() => {
    if (!data?.fresh_verify) return;
    setFverify((prev) => {
      const next = { ...prev };
      data.fresh_verify.forEach((g) => g.items.forEach((it) => {
        const sk = `${g.branch_id}:${it.product_id}`;
        if (next[sk] === undefined) {
          next[sk] = {
            received: it.verified ? (it.received_qty ?? it.ordered_qty) : it.ordered_qty,
            damaged: it.verified ? (it.damaged_qty ?? 0) : 0,
          };
        }
      }));
      return next;
    });
  }, [data]);

  const advance = async (action) => {
    setBusy(action);
    try { const { data: r } = await api.post("/workspace/central/advance", { action }); toast.success(`${r.updated} ${t("orders updated")}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  const check = async (context, scope_key, label, checked, extra = {}) => {
    try { await api.post("/workspace/physical-check", { context, scope_key, label, checked: !checked, ...extra }); load(); }
    catch (e) { toast.error(t("Failed")); }
  };

  const verifyFresh = async (g, it) => {
    const sk = `${g.branch_id}:${it.product_id}`;
    const v = fverify[sk] || { received: it.ordered_qty, damaged: 0 };
    setBusy(sk);
    try {
      const { data: r } = await api.post("/workspace/fresh-verify", {
        branch_id: g.branch_id, product_id: it.product_id, product_name: it.name,
        ordered_qty: it.ordered_qty, received_qty: Number(v.received) || 0, damaged_qty: Number(v.damaged) || 0,
      });
      toast.success(r.variance_created ? `${it.name} · ${t("Variance created")}` : `${it.name} · ${t("Verified")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(null); }
  };

  const assign = async (orderId, driverId) => {
    if (!driverId) return;
    try { const { data: r } = await api.post(`/orders/${orderId}/assign-driver`, { driver_id: driverId }); toast.success(`${t("Assigned to")} ${r.assigned_driver_name}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const dispatch = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/dispatch`);
      toast.success(t("Shipment dispatched — awaiting branch receive"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  const openSj = (orderId) => printDoc(`/orders/${orderId}/surat-jalan`, { key: orderId, label: t("Surat Jalan") });
  const printFresh = () => printDoc("/workspace/fresh-shopping-list/pdf", { key: "fresh-print", label: t("Fresh Market Shopping List") });
  const exportFresh = () => exportDoc("/workspace/fresh-shopping-list/pdf", { key: "fresh-export", label: t("Fresh Market Shopping List") });

  if (!data) return <PageLoader />;
  const s = data.summary;

  return (
    <div className="animate-fade-up space-y-5 pb-24 lg:pb-2" data-testid="central-morning">
      <PageHeader icon={Sunrise} title={t("Central Operations Morning")} subtitle={t("Logistics — receive, verify, pack & dispatch")} />
      <OpsProgress items={data.progress} testid="cm-progress" />

      {/* Outstanding work — what is still waiting at each stage */}
      {data.pending && (
        <div data-testid="cm-pending">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-ink-mute">{t("Outstanding Work")}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
            {[
              { key: "fresh_market", label: t("Fresh Market"), n: data.pending.fresh_market, sub: t("items waiting"), icon: Sprout },
              { key: "warehouse_pick", label: t("Warehouse Picking"), n: data.pending.warehouse_pick, sub: t("items remaining"), icon: ClipboardCheck },
              { key: "packing", label: t("Packing"), n: data.pending.packing, sub: t("orders waiting"), icon: FileText },
              { key: "delivery", label: t("Delivery"), n: data.pending.delivery_unassigned, sub: t("drivers unassigned"), icon: Truck },
              { key: "confirmation", label: t("Branch Confirmation"), n: data.pending.branch_confirm, sub: t("branches waiting"), icon: Store },
            ].map((p) => (
              <div key={p.key} className={`rounded-xl border p-3 ${p.n > 0 ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`} data-testid={`cm-pending-${p.key}`}>
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-soft"><p.icon size={13} /> <span className="truncate">{p.label}</span></div>
                <p className={`font-heading text-2xl font-extrabold leading-tight ${p.n > 0 ? "text-amber-700" : "text-emerald-700"}`}>{p.n}</p>
                <p className="text-[10px] text-ink-mute">{p.n > 0 ? p.sub : t("All clear")}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 1 — Receive & Verify Fresh Market Goods (grouped by branch) */}
      <Step n={1} title={t("Fresh Market Verification")} icon={Sprout} done={data.fresh_total > 0 && data.fresh_done === data.fresh_total} testid="cm-step1">
        <p className="mb-3 text-xs text-ink-mute">{t("Verify each branch's fresh goods on arrival. A shortage or damage automatically creates a variance before packing.")}</p>
        <div className="mb-3 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" loading={printing === "fresh-print"} onClick={printFresh} data-testid="cm-print-fresh"><Printer size={14} /> {t("Print")}</Button>
          <Button variant="outline" size="sm" loading={printing === "fresh-export"} onClick={exportFresh} data-testid="cm-export-fresh"><Download size={14} /> {t("Export PDF")}</Button>
        </div>
        {(!data.fresh_verify || data.fresh_verify.length === 0) ? (
          <p className="py-4 text-center text-sm text-ink-mute">{t("No fresh market items today")}</p>
        ) : (
          <div className="space-y-3">
            {data.fresh_verify.map((g) => (
              <div key={g.branch_id} className="overflow-hidden rounded-xl border border-line" data-testid={`cm-fresh-branch-${g.branch_id}`}>
                <div className="flex items-center gap-2 border-b border-line bg-emerald-50 px-4 py-2">
                  <Sprout size={15} className="text-emerald-600" />
                  <p className="font-bold text-ink">{g.branch_name}</p>
                  {g.complete ? <Badge tone="success" className="ml-auto"><CheckCircle2 size={12} /> {t("Verified")}</Badge>
                    : <Badge tone="warn" className="ml-auto">{g.total - g.verified} {t("missing")}</Badge>}
                </div>
                <div className="divide-y divide-line">
                  {g.items.map((it) => {
                    const sk = `${g.branch_id}:${it.product_id}`;
                    const v = fverify[sk] || { received: it.ordered_qty, damaged: 0 };
                    return (
                      <div key={it.product_id} className="px-4 py-2.5" data-testid={`cm-fresh-${g.branch_id}-${it.product_id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <p className="min-w-0 flex-1 truncate font-medium text-ink">{it.name}</p>
                          {it.verified && (it.has_variance
                            ? <Badge tone="danger"><AlertTriangle size={11} /> {t("Shortage")}</Badge>
                            : <Badge tone="success"><CheckCircle2 size={11} /> {t("Verified")}</Badge>)}
                        </div>
                        <div className="mt-2 flex flex-wrap items-end gap-3">
                          <div><p className="text-[10px] font-semibold uppercase text-ink-mute">{t("Ordered Qty")}</p><p className="text-sm font-bold text-ink">{fmtNum(it.ordered_qty)} {it.unit}</p></div>
                          <label className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-ink-mute">{t("Received Qty")}</span>
                            <input type="number" min="0" value={v.received} onChange={(e) => setFverify((p) => ({ ...p, [sk]: { ...v, received: e.target.value } }))} className="cf-input !py-1.5 w-24 text-right text-sm" data-testid={`cm-fresh-received-${g.branch_id}-${it.product_id}`} /></label>
                          <label className="flex flex-col"><span className="text-[10px] font-semibold uppercase text-ink-mute">{t("Damaged Qty")}</span>
                            <input type="number" min="0" value={v.damaged} onChange={(e) => setFverify((p) => ({ ...p, [sk]: { ...v, damaged: e.target.value } }))} className="cf-input !py-1.5 w-24 text-right text-sm" data-testid={`cm-fresh-damaged-${g.branch_id}-${it.product_id}`} /></label>
                          <Button size="sm" variant={it.verified ? "outline" : "primary"} loading={busy === sk} onClick={() => verifyFresh(g, it)} data-testid={`cm-fresh-verify-${g.branch_id}-${it.product_id}`}>
                            <CheckCircle2 size={14} /> {it.verified ? t("Re-verify") : t("Verify")}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </Step>

      {/* Step 2 — Verify Warehouse Picking */}
      <Step n={2} title={t("Verify Warehouse Picking")} icon={ClipboardCheck} done={data.verify_total > 0 && data.verify_done === data.verify_total} testid="cm-step2">
        <div className="space-y-3">
          {data.verify.length === 0 ? <p className="py-4 text-center text-sm text-ink-mute">{t("Nothing to verify yet")}</p> :
            data.verify.map((g) => (
              <div key={g.branch_id} className="rounded-xl border border-line" data-testid={`cm-verify-${g.branch_id}`}>
                <div className="flex items-center gap-2 border-b border-line bg-app px-4 py-2">
                  <p className="font-bold text-ink">{g.branch_name}</p>
                  {g.complete ? <Badge tone="success"><CheckCircle2 size={12} /> {t("Complete")}</Badge>
                    : <Badge tone="warn"><AlertTriangle size={12} /> {g.total - g.checked} {t("missing")}</Badge>}
                  <div className="ml-auto flex items-center gap-1.5">
                    <Button size="sm" variant="outline" loading={printing === `pick-print-${g.branch_id}`}
                      onClick={() => printDoc("/workspace/branch-picklist/pdf", { key: `pick-print-${g.branch_id}`, label: `${g.branch_name} · ${t("Picking List")}`, params: { branch_id: g.branch_id } })}
                      data-testid={`cm-print-pick-${g.branch_id}`}><Printer size={14} /> {t("Print")}</Button>
                    <Button size="sm" variant="outline" loading={printing === `pick-export-${g.branch_id}`}
                      onClick={() => exportDoc("/workspace/branch-picklist/pdf", { key: `pick-export-${g.branch_id}`, label: `${g.branch_name} · ${t("Picking List")}`, params: { branch_id: g.branch_id } })}
                      data-testid={`cm-export-pick-${g.branch_id}`}><Download size={14} /> {t("Export PDF")}</Button>
                  </div>
                </div>
                <div className="divide-y divide-line">
                  {g.items.map((it) => (
                    <label key={it.scope_key} className="flex cursor-pointer items-center gap-3 px-4 py-2 hover:bg-app" data-testid={`cm-verifyitem-${it.scope_key}`}>
                      <input type="checkbox" checked={it.checked} onChange={() => check("pick_verify", it.scope_key, `${g.branch_name} · ${it.name}`, it.checked, { branch_id: g.branch_id, product_id: it.product_id })} className="h-4 w-4 rounded border-line text-primary focus:ring-primary" />
                      <span className={`min-w-0 flex-1 truncate text-sm ${it.checked ? "text-ink line-through opacity-60" : "text-ink"}`}>{it.name}</span>
                      <span className="text-sm font-bold text-ink-soft">{it.qty} {it.unit}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </Step>

      {/* Step 3 — Packing & Delivery Notes */}
      <Step n={3} title={t("Packing & Delivery Notes")} icon={FileText} done={s.to_pack === 0 && s.to_dispatch + s.delivered > 0} testid="cm-step3">
        {[...data.packing, ...data.ready_to_pack].length === 0 ? <p className="py-4 text-center text-sm text-ink-mute">{t("All packed")}</p> : (
          <div className="space-y-2">
            {[...data.packing, ...data.ready_to_pack].map((o) => (
              <div key={o.order_id} onClick={() => setViewId(o.order_id)} className="flex cursor-pointer items-center gap-3 rounded-xl border border-line px-4 py-2.5 transition hover:bg-app" data-testid={`cm-pack-${o.order_no}`}>
                <div className="min-w-0 flex-1"><p className="font-bold text-ink">{o.branch_name}</p><p className="text-xs text-ink-mute">{o.item_count} {t("items")} · {o.fulfillment_status || "PICKING"} · <span className="font-mono">Ref: {o.order_no}</span></p></div>
                <Button variant="outline" size="sm" loading={printing === o.order_id} onClick={(e) => { e.stopPropagation(); openSj(o.order_id); }} data-testid={`cm-sj-${o.order_no}`}><FileText size={14} /> {t("Surat Jalan")}</Button>
              </div>
            ))}
          </div>
        )}
        {!data.can_pack && (
          <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700" data-testid="cm-pack-gate">
            <AlertTriangle size={13} className="mr-1 inline" /> {t("Complete fresh & picking verification before packing")}
          </p>
        )}
        <div className="mt-4 flex gap-2">
          <Button className="flex-1" disabled={!data.can_pack} loading={busy === "pack"} onClick={() => advance("pack")} data-testid="cm-pack-done">
            <ClipboardCheck size={16} /> {t("Verify Done → Packing")}
          </Button>
          <Button className="flex-1" variant="outline" loading={busy === "ready"} onClick={() => advance("ready")} data-testid="cm-ready">
            <PackageCheck size={16} /> {t("Mark Packed & Ready")}
          </Button>
        </div>
      </Step>

      {/* Step 4 — Dispatch */}
      <Step n={4} title={t("Dispatch Deliveries")} icon={Truck} done={s.to_dispatch === 0 && s.delivered > 0} testid="cm-step4">
        {data.dispatch.length === 0 ? <p className="py-4 text-center text-sm text-ink-mute">{t("Nothing to dispatch")}</p> : (
          <div className="space-y-2">
            {data.dispatch.map((o) => (
              <div key={o.order_id} onClick={() => setViewId(o.order_id)} className="flex cursor-pointer flex-wrap items-center gap-3 rounded-xl border border-line px-4 py-2.5 transition hover:bg-app" data-testid={`cm-dispatch-${o.order_no}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-ink">{o.branch_name} <Badge tone={STAGE_TONE[o.fulfillment_status] || "mute"} className="ml-1">{t(o.fulfillment_status)}</Badge></p>
                  <p className="text-xs text-ink-mute">{o.item_count} {t("items")} · <span className="font-mono">Ref: {o.order_no}</span></p>
                </div>
                {o.assigned_driver_id && (
                  <Badge tone="mute" data-testid={`cm-assigned-${o.order_no}`}><UserCheck size={13} /> {o.assigned_driver_name}</Badge>
                )}
                <Button size="sm" onClick={(e) => { e.stopPropagation(); dispatch(o.order_id); }} data-testid={`cm-send-${o.order_no}`}>
                  <Truck size={14} /> {t("Send")}
                </Button>
              </div>
            ))}
          </div>
        )}
      </Step>

      {/* Step 5 — Branch Confirmation */}
      <Step n={5} title={t("Wait for Branch Confirmation")} icon={Store} done={data.all_done} testid="cm-step5">
        {data.variances.length > 0 && (
          <div className="mb-3 rounded-xl border border-danger bg-danger-light px-4 py-2.5 text-sm font-semibold text-danger-text" data-testid="cm-variances">
            <AlertTriangle size={15} className="mr-1 inline" /> {data.variances.length} {t("open variances need attention")}
          </div>
        )}
        <div className="space-y-2">
          {data.confirm.length === 0 ? <p className="py-4 text-center text-sm text-ink-mute">{t("No deliveries out yet")}</p> :
            data.confirm.map((o) => (
              <div key={o.order_id} onClick={() => setViewId(o.order_id)} className="flex cursor-pointer items-center gap-3 rounded-xl border border-line px-4 py-2.5 transition hover:bg-app" data-testid={`cm-confirm-${o.order_no}`}>
                <div className="min-w-0 flex-1"><p className="font-bold text-ink">{o.branch_name}</p><p className="text-xs text-ink-mute font-mono">Ref: {o.order_no}</p></div>
                <Badge tone={o.status === "RECEIVED" ? "success" : "warn"}>{o.status === "RECEIVED" ? t("Completed") : t("Delivered")}</Badge>
              </div>
            ))}
        </div>
        <Button variant="outline" className="mt-4 w-full" onClick={() => navigate("/control-tower")} data-testid="cm-control-tower">
          {t("Open in Control Tower")} <ArrowRight size={15} />
        </Button>
      </Step>

      <OrderDetailModal orderId={viewId} onClose={() => setViewId(null)} />
    </div>
  );
}
